import { Component, ErrorInfo, ReactNode } from 'react'
import { Banner } from './Banner'
import { Button } from './Button'
import buttonStyles from './Button.module.scss'
import styles from './ErrorBoundary.module.scss'

interface Props {
    children: ReactNode
}

interface State {
    hasError: boolean
    error: Error | null
}

/**
 * Top-level error boundary that catches unhandled errors during rendering.
 * This prevents the app from going white when something unexpected throws.
 */
class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props)
        this.state = { hasError: false, error: null }
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error }
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error('ErrorBoundary caught an error:', error)
        console.error('Component stack:', errorInfo.componentStack)
        // TODO: Send to error tracking service (Sentry, etc.)
    }

    handleReload = () => {
        window.location.reload()
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className={styles.errorBoundary}>
                    <div className={styles.content}>
                        <h1>Out of Bounds</h1>
                        <p>We&apos;ve logged the fault and will be back on serve shortly.</p>

                        {import.meta.env.DEV && this.state.error && (
                            <div className={styles.devError}>
                                <Banner variant="error" message={this.state.error.message} />
                                <pre>{this.state.error.stack}</pre>
                            </div>
                        )}

                        <div className={styles.actions}>
                            <Button onClick={this.handleReload}>Reload Page</Button>
                            <a href="/" className={`${buttonStyles.button} ${buttonStyles.secondary} ${buttonStyles.medium}`}>
                                Go to Home
                            </a>
                        </div>
                    </div>
                </div>
            )
        }

        return this.props.children
    }
}

export default ErrorBoundary
